import { useEffect, useMemo, useRef, useState } from 'react';
import { List, ListItem, Collapse, ListItemText, debounce } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { pages } from 'pages/documentation/pageData';
import { SearchDocsWrapper, DocsBtn, ListItemDocs, MenuWrapper, SidebarDropdown, SidebarDropdownIcon, SidebarItem, SidebarItemLink, SidebarMenuWrapper, SidebarSection, SidebarSectionTitle } from './styles';
import AsyncSelectComponent from 'shared-compoents/react-select';
import { searchDocs } from 'services/confluenceService';
import { SearchResult, SelectOption } from 'utility/types';
import { SearchResultLink } from 'utility/styles';
import { GroupBase, InputActionMeta, OptionsOrGroups } from 'react-select';

function SidebarMenu() {
  const [searchValue, setSearchValue] = useState("");
  // const { closeSidebar } = useContext(SidebarContext);
  const SIDEBAR_SELECTED = "sidebar-selected";
  const [openTDSP, setOpenTDSP] = useState(false);
  // const [isOnboardingDropdownActive, setIsOnboardingDropdownActive] = useState(true);
  const [openPages, setOpenPages] = useState<{ [key: number]: boolean }>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { pageId } = useParams();
  const activeItemRef = useRef<HTMLDivElement>(null);
  const scrolledPageIdRef = useRef<string | null>(null);

  // Auto-expand sidebar and highlight selected page on route change
  useEffect(() => {
    if (!pageId) return;

    // Find the parent section containing this pageId
    const parentPage = pages.find(
      (page) =>
        page.children?.some((child) => child.page_id === pageId)
    );

    if (parentPage) {
      // Expand "Platform Docs" and the parent section
      setOpenTDSP(true);
      setOpenPages((prev) => ({ ...prev, [parentPage.id]: true }));
    }
  }, [pageId]);

  // Scroll to active item after section expands.
  // Only scroll once per selected page. `openPages` is kept as a dependency so
  // the scroll can run after the parent section expands on a route change, but
  // the ref guard prevents re-scrolling when the user manually toggles another
  // opener in the sidebar.
  useEffect(() => {
    if (!pageId) return;
    if (scrolledPageIdRef.current === pageId) return;
    if (!activeItemRef.current) return;
    const timer = setTimeout(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledPageIdRef.current = pageId;
      }
    }, 350); // wait for Collapse animation
    return () => clearTimeout(timer);
  }, [pageId, openPages]);

  const handleTDSPClick = () => {
    setOpenTDSP(!openTDSP);
  };

  // const handleOnboardingDropdownClick = () => {
  //   setIsOnboardingDropdownActive(!isOnboardingDropdownActive);
  // };

  const handlePageClick = (id: number) => {
    setOpenPages((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const tdspPages = pages;
  const handlePageSelect = (page: { page_id: string }) => {
    navigate(`/documentation/${page.page_id}`);
  };

  const formatOptionLabel = ({ value, label }: SelectOption) => (
    <div style={{ display: "flex" }}>
      <SearchResultLink to={`/documentation/${value}`} className="content" style={{ marginLeft: "10px", color: "black", textDecoration: "none"}} dangerouslySetInnerHTML={{__html: label}} ></SearchResultLink>
    </div>
  );
  
  const searchDocsHandler = async (inputValue: string) => {
    try {
      const response: SearchResult[] = await searchDocs(inputValue);
      return response ? response.map((responseItem: SearchResult) => ({
        label: responseItem.title.replace("@@@hl@@@", "<span>").replace("@@@endhl@@@", "</span>"),
        value: responseItem.page_id
      })) : []
    } catch(error) {
      console.log("ERROR: ", error);
      return []
    }
  };


  const _loadSuggestions = (inputValue: string, callback: (options: OptionsOrGroups<{ label: string; value: string; }, GroupBase<{ label: string; value: string; }>>) => void) => {
    searchDocsHandler(inputValue)
      .then((resp) => callback(resp));
  }; 

  // eslint-disable-next-line
  const loadSuggestions = useMemo(() => debounce(_loadSuggestions, 800), []);

  const onSearchInputChange = (newValue: string, action: InputActionMeta) => {
    if (action.action === "input-change") {
      setSearchValue(newValue);
    }
    if (action.action === "set-value") {
      return searchValue;
    }
    if (action.action === "menu-close") {
      return action.prevInputValue;
    }
  };

  return (
      <MenuWrapper>
      <SidebarMenuWrapper>
        <li>
          <div>
              <SidebarSectionTitle>Knowledge Hub</SidebarSectionTitle>
              <SidebarSection>
                {/* <li>
                  <SidebarDocsSearchWrapper>
                    <SidebarDocsSearchInput type="text" placeholder='Search Docs' />
                    <SidebarDocsSearchBtn><img src="/assets/search-icon.svg" alt='Search Icon' width={12} height={12} /></SidebarDocsSearchBtn>
                  </SidebarDocsSearchWrapper>
                </li> */}
                <li>
                  <SearchDocsWrapper>
                    <AsyncSelectComponent
                      onInputChange={onSearchInputChange}
                      inputValue={searchValue}
                      selectedValue={{ label: "", value: "" }}
                      selectOptions={[]}
                      promiseOptions={loadSuggestions}
                      formatOptionLabel={formatOptionLabel}
                      placeholder="Search Docs"
                    />
                  </SearchDocsWrapper>
                </li>
                <SidebarItem>
                  <SidebarItemLink to="#" onClick={handleTDSPClick}>
                    <img src="/assets/platform-docs-icon.svg" alt='Platform Docs' /> <span>Platform Docs</span><SidebarDropdown><SidebarDropdownIcon className={ openTDSP ? "dropdown-open" : "" } src='/assets/dropdown-icon.svg' /></SidebarDropdown>
                  </SidebarItemLink>
                  <Collapse in={openTDSP} timeout="auto" unmountOnExit>
                    <List component="div">
                      {tdspPages.map((page) => (
                        <div key={page.id}>
                          <ListItemDocs
                            onClick={() => handlePageClick(page.id)}
                          >
                            <ListItemText primary={page.title} />
                            {page.children ? (
                              <SidebarDropdown><SidebarDropdownIcon className={ openPages[page.id] ? "dropdown-open" : "" } src='/assets/dropdown-icon.svg' /></SidebarDropdown>
                            ) : null}
                          </ListItemDocs>
                          {page.children && (
                            <Collapse
                              in={openPages[page.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <List component="div" disablePadding>
                                {page.children.map((subPage) => (
                                  <ListItem
                                    key={subPage.id}
                                    component="div"
                                    ref={pageId === subPage.page_id ? activeItemRef : null}
                                  >
                                    <DocsBtn
                                      onClick={() => handlePageSelect(subPage)}
                                      className={ pageId === subPage.page_id ? SIDEBAR_SELECTED : "" }
                                    >
                                      <ListItemText primary={subPage.title} />
                                    </DocsBtn>
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          )}
                        </div>
                      ))}
                    </List>
                  </Collapse>
                </SidebarItem>
                {/* <SidebarItem className={ location.pathname === "/faq" ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/faq"><img src="/assets/faq-icon.svg" alt='FAQ Icon' /> <span>FAQ</span></SidebarItemLink>
                </SidebarItem> */}
              </SidebarSection>
          </div>
        </li>
        <li>
          <div>
              <SidebarSectionTitle>Self Service</SidebarSectionTitle>
              <SidebarSection>
                <SidebarItem className={ location.pathname.includes("/self-service-portal") ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/self-service-portal"><img src="/assets/template-icon.svg" alt='MLOps Pipeline Templates' /> <span>Self Service Portal</span></SidebarItemLink>
                </SidebarItem>
                <SidebarItem className={ location.pathname.includes("/track-requests") ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/track-requests"><img src="/assets/track-request.svg" alt='MLOps Pipeline Templates' /> <span>Track Requests</span></SidebarItemLink>
                </SidebarItem>
              </SidebarSection>
          </div>
        </li>
        <li>
          <div>
              <SidebarSectionTitle>Dashboards</SidebarSectionTitle>
              <SidebarSection>
                <SidebarItem className={ location.pathname.includes("/tdsp-inventory-dashboard") ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/tdsp-inventory-dashboard"><img src="/assets/inventory-dashboard-icon.svg" alt='Inventory Dashboard' /> <span>Inventory Dashboard</span></SidebarItemLink>
                </SidebarItem>
                <SidebarItem className={ location.pathname.includes("/tdsp-cost-dashboard") ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/tdsp-cost-dashboard"><img src="/assets/cost-dashboard-icon.svg" alt='Cost Dashboard' /> <span>Cost Dashboard</span></SidebarItemLink>
                </SidebarItem>
                <SidebarItem className={ location.pathname.includes("/platform-dashboard") ? SIDEBAR_SELECTED : "" }>
                  <SidebarItemLink to="/platform-dashboard"><img src="/assets/platform-dashboard-icon.svg" alt='Platform Dashboard' /> <span>Platform Dashboard</span></SidebarItemLink>
                </SidebarItem>
              </SidebarSection>
          </div>
        </li>
        <SidebarItem className={ location.pathname === "/raise-inquiry" ? SIDEBAR_SELECTED : "" }>
          <SidebarItemLink to="/raise-inquiry"><img src="/assets/user-request-icon.svg" alt='MLOps Pipeline Templates' /> <span>Raise Inquiry</span></SidebarItemLink>
        </SidebarItem>
      </SidebarMenuWrapper>
      </MenuWrapper>
  );
}

export default SidebarMenu;
